import { useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useT } from '../i18n';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { KeyRound, Lock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export function SecuritySettingsCard() {
    const { user, logout } = useAuth();
    const t = useT();
    const [open, setOpen] = useState(false);
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');
    const [busy, setBusy] = useState(false);
    const [openBroadcast, setOpenBroadcast] = useState(false);
    const [broadcastNext, setBroadcastNext] = useState('');
    const [broadcastConfirm, setBroadcastConfirm] = useState('');
    const [broadcastBusy, setBroadcastBusy] = useState(false);
    const [deleteBusy, setDeleteBusy] = useState(false);

    const deleteAccount = async () => {
        const warning = 'Aquesta acció és DEFINITIVA. Perdràs el compte, vídeos, vendes, saldo de cartera i qualsevol diner no retirat. No es pot recuperar. Vols continuar?';
        if (!window.confirm(warning)) return;
        const confirmation = window.prompt('Escriu ELIMINAR per confirmar:');
        if (confirmation?.trim().toUpperCase() !== 'ELIMINAR') return;
        const password = window.prompt('Escriu la teva contrasenya actual:');
        setDeleteBusy(true);
        try {
            await api.delete('/users/me/account', { data: { confirmation, password } });
            logout();
            window.location.href = '/';
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s’ha pogut eliminar el compte');
        } finally { setDeleteBusy(false); }
    };

    if (!user) return null;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (next.length < 6) {
            toast.error('Mínim 6 caràcters / At least 6 characters');
            return;
        }
        if (next !== confirm) {
            toast.error("Les contrasenyes no coincideixen / Passwords don't match");
            return;
        }
        setBusy(true);
        try {
            const body = { new_password: next };
            // Only send current_password if the user already has one set
            if (current) body.current_password = current;
            await api.post('/auth/change-password', body);
            toast.success(t('common.signed_in') === 'Signed in' ? 'Password updated' : 'Contrasenya actualitzada');
            setCurrent(''); setNext(''); setConfirm(''); setOpen(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || t('common.error'));
        } finally { setBusy(false); }
    };

    const onBroadcastSubmit = async (e) => {
        e?.preventDefault();
        if (broadcastNext.length > 0 && broadcastNext !== broadcastConfirm) {
            toast.error("Les contrasenyes no coincideixen / Passwords don't match");
            return;
        }
        setBroadcastBusy(true);
        try {
            // Empty string unsets the broadcast password
            await api.patch('/users/me', { broadcast_password: broadcastNext });
            toast.success('Broadcast password updated');
            setBroadcastNext(''); setBroadcastConfirm(''); setOpenBroadcast(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error');
        } finally { setBroadcastBusy(false); }
    };

    return (
        <Card data-testid="security-card" className="p-5 mb-6 bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <Lock className="h-4 w-4 text-[color:var(--alb-sun)]" />
                    <h3 className="font-display font-semibold text-base md:text-lg">Seguretat · Security</h3>
                </div>
                <Button data-testid="open-change-password" variant="outline" onClick={() => setOpen((x) => !x)} className="border-[color:var(--alb-hairline)] hover:bg-[color:var(--alb-surface-2)]">
                    <KeyRound className="h-4 w-4 mr-1" /> {open ? 'Tanca / Close' : 'Canvia contrasenya / Change password'}
                </Button>
            </div>
            {open && (
                <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                    <div className="sm:col-span-1">
                        <Label htmlFor="cur-pw">Actual / Current</Label>
                        <Input data-testid="current-password-input" id="cur-pw" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)]" placeholder="(buit si Google / empty if Google)" />
                    </div>
                    <div>
                        <Label htmlFor="new-pw">Nova / New</Label>
                        <Input data-testid="new-password-input" id="new-pw" type="password" required minLength={6} value={next} onChange={(e) => setNext(e.target.value)} className="bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)]" />
                    </div>
                    <div>
                        <Label htmlFor="conf-pw">Confirma / Confirm</Label>
                        <Input data-testid="confirm-password-input" id="conf-pw" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)]" />
                    </div>
                    <div className="sm:col-span-3 flex justify-end">
                        <Button data-testid="submit-change-password" type="submit" disabled={busy} className="bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)] hover:bg-[color:var(--alb-tangerine)]">
                            {busy ? '...' : 'Desa / Save'}
                        </Button>
                    </div>
                </form>
            )}
            <div className="mt-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <KeyRound className="h-4 w-4 text-[color:var(--alb-sun)]" />
                        <h3 className="font-display font-semibold text-base md:text-lg">Contrasenya d'emissió (per a +18)</h3>
                    </div>
                    <Button variant="outline" onClick={() => setOpenBroadcast(x => !x)} className="border-[color:var(--alb-hairline)] hover:bg-[color:var(--alb-surface-2)]">
                        {openBroadcast ? 'Tanca' : 'Configura'}
                    </Button>
                </div>
                {openBroadcast && (
                    <form onSubmit={onBroadcastSubmit} className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                        <div>
                            <Label htmlFor="b-new">Nova contrasenya d'emissió</Label>
                            <Input id="b-new" type="password" value={broadcastNext} onChange={(e) => setBroadcastNext(e.target.value)} className="bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)]" />
                        </div>
                        <div>
                            <Label htmlFor="b-conf">Confirma</Label>
                            <Input id="b-conf" type="password" value={broadcastConfirm} onChange={(e) => setBroadcastConfirm(e.target.value)} className="bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)]" />
                        </div>
                        <div className="sm:col-span-3 flex justify-end">
                            <Button type="button" onClick={onBroadcastSubmit} disabled={broadcastBusy} className="bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)] hover:bg-[color:var(--alb-tangerine)]">{broadcastBusy ? '...' : 'Desa / Save'}</Button>
                        </div>
                        <div className="sm:col-span-3 text-xs text-[color:var(--alb-text-muted)] mt-2">La contrasenya d'emissió només s'aplica a emissions +18 i serà requerida per accedir-hi.</div>
                    </form>
                )}
            </div>
            <div className="mt-5 border-t border-red-500/30 pt-4">
                <h3 className="font-display font-semibold text-red-400">Eliminar compte</h3>
                <p className="mt-1 text-xs leading-5 text-[color:var(--alb-text-muted)]">Perdràs definitivament el saldo, els diners no retirats, vídeos, vendes, comentaris i totes les dades.</p>
                <Button type="button" variant="destructive" disabled={deleteBusy} onClick={deleteAccount} className="mt-3"><Trash2 className="mr-2 h-4 w-4" />{deleteBusy ? 'Eliminant...' : 'Eliminar el meu compte'}</Button>
            </div>
        </Card>
    );
}
